import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package2,
  ListOrdered,
  ClipboardList,
  Boxes,
  ArrowRightLeft,
  Building2,
  Menu,
  ChevronFirst,
  ChevronLast,
  LogOut,
  ShoppingCart,
  FileText,
  FileBarChart,
  Settings,
  Users,
  Tags,
  PackageOpen,
  ArrowLeftRight,
  ClipboardCheck,
  X,
  ListChecks,
  Building,
  FileSpreadsheet,
  Search,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/providers/AuthProvider";

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar = ({ onClose }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, isAdmin } = useAuth();
  const { toast } = useToast();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      toast({
        title: "Erro ao sair",
        description: "Ocorreu um erro ao tentar sair do sistema.",
        variant: "destructive",
      });
    }
  };

  const adminMenuItems = [
    {
      title: "Painel",
      icon: LayoutDashboard,
      path: "/dashboard",
    },
    {
      title: "Produtos",
      icon: Package2,
      path: "/products",
    },
    {
      title: "Categorias",
      icon: Tags,
      path: "/categories",
    },
    {
      title: "Estoque",
      icon: PackageOpen,
      path: "/stock",
    },
    {
      title: "Movimentações",
      icon: ArrowLeftRight,
      path: "/movements",
    },
    {
      title: "Cadastro",
      icon: Building2,
      path: "/cadastro",
    },
    {
      title: "Fornecedores",
      icon: Building,
      path: "/suppliers",
    },
    /*{
      title: "Obras",
      icon: Building2,
      path: "/construction",
    },
    {
      title: "Secretarias",
      icon: Users,
      path: "/departments",
    },*/
    {
      title: "Relatórios",
      icon: FileBarChart,
      path: "/reports",
    },
    {
      title: "Pedidos",
      icon: ClipboardList,
      path: "/orders/management",
    },
    {
      title: "Configurações",
      icon: Settings,
      path: "/settings",
    },
  ];

  const userMenuItems = [
    {
      title: "Painel",
      icon: LayoutDashboard,
      path: "/user-dashboard",
    },
    {
      title: "Meus Pedidos",
      icon: ClipboardList,
      path: "/my-orders",
    },
    {
      title: "Configurações",
      icon: Settings,
      path: "/user/settings",
    },
  ];

  const menuItems = isAdmin ? adminMenuItems : userMenuItems;

  // Fecha o menu quando clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const mobileMenu = document.getElementById('mobile-menu');
      const menuButton = document.getElementById('mobile-menu-button');
      
      if (mobileMenu && !mobileMenu.contains(event.target as Node) && 
          menuButton && !menuButton.contains(event.target as Node)) {
        // setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <aside className={cn(
      "h-screen bg-[#1e1e2d] text-white border-r border-[#2b2b3d] transition-all duration-300",
      isCollapsed ? "w-[70px]" : "w-[250px]"
    )}>
      <nav className="h-full flex flex-col">
        <div className="p-4 pb-2 flex justify-between items-center border-b border-[#2b2b3d]">
          <h2
            className={cn(
              "font-semibold text-lg overflow-hidden transition-all",
              isCollapsed ? "w-0" : "w-52"
            )}
          >
            Sistema de {isAdmin ? "Estoque" : "Pedidos"}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex text-white hover:bg-[#2b2b3d]"
            >
              {isCollapsed ? (
                <ChevronFirst className="h-5 w-5" />
              ) : (
                <ChevronLast className="h-5 w-5" />
              )}
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="lg:hidden text-white hover:bg-[#2b2b3d]"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-0.5">
          {(isAdmin ? adminMenuItems : userMenuItems).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Button
                key={item.path}
                variant="ghost"
                className={cn(
                  "relative flex items-center gap-2 px-3 py-2 w-full justify-start text-white group",
                  isActive && "bg-[#2b2b3d] font-medium",
                  !isActive && "hover:bg-[#2b2b3d]"
                )}
                onClick={() => navigate(item.path)}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className={cn(
                  "transition-all duration-300",
                  isCollapsed ? "w-0 overflow-hidden" : "w-full"
                )}>
                  {item.title}
                </span>
                {isCollapsed && (
                  <div className="absolute left-full rounded-md px-2 py-1 ml-6 bg-[#1e1e2d] text-sm invisible opacity-20 -translate-x-3 transition-all group-hover:visible group-hover:opacity-100 group-hover:translate-x-0">
                    {item.title}
                  </div>
                )}
              </Button>
            );
          })}
        </div>

        <div className="border-t border-[#2b2b3d] p-3">
          <Button
            variant="ghost"
            className="w-full justify-start text-white hover:bg-[#2b2b3d]"
            onClick={handleSignOut}
          >
            <LogOut className="mr-2 h-5 w-5" />
            <span className={cn(
              "transition-all duration-300",
              isCollapsed ? "w-0 overflow-hidden" : "w-full"
            )}>
              Sair
            </span>
          </Button>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
